import React, { useState, useEffect } from "react";
import { Input, DatePicker, Tag, Select, Button, message } from "antd";
import ModalWrapper from "../../Modal";
import dayjs from "dayjs";
import { putRequest } from "../../service/index";

const { TextArea } = Input;

const EditInventoryModal = (props) => {
    
    const initialFormData = {
        productName: "",
        quantity: "",
        productType: "",
        price: "",
        purchaseDate: null,
        serialNos: [],
        description: "",
        remaining: "",
    };
    const { open, onRequestClose, data } = props;
    const [formData, setFormData] = useState(initialFormData);
    const [originalQuantity, setOriginalQuantity] = useState(0);
    useEffect(() => {
        if (data) {
            setFormData({
                productName: data.productName || "",
                quantity: data.quantity || "",
                productType: data.productType || "",
                price: data.price || "",
                purchaseDate: data.purchaseDate ? dayjs(data.purchaseDate) : null,
                serialNos: data.serialNos || [],
                description: data.description || "",
                remaining: data.remaining || "",
            });
            setOriginalQuantity(Number(data.quantity) || 0);
        } else {
            setFormData(initialFormData);
            setOriginalQuantity(0);
        }
    }, [data, open]);

    // Get assigned serial numbers from assignedDetails
    const assignedSerialNumbers = Array.isArray(data?.assignedDetails)
        ? data.assignedDetails.map(item => item.serialNumber).filter(Boolean)
        : [];

    // Combine all serial numbers for display in Select (assigned + unassigned)
    const allSerialNumbers = Array.from(new Set([...(formData.serialNos || []), ...assignedSerialNumbers]));

    const handleSerialNosChange = (value) => {
        // Prevent removing assigned serial numbers
        const filtered = value.filter(sn => !assignedSerialNumbers.includes(sn));
        setFormData((prev) => ({
            ...prev,
            serialNos: filtered,
        }));
    };

    const handleInputChange = (field, value) => {
        if (field === "quantity") {
            const newQuantity = Number(value);
            const diff = newQuantity - originalQuantity;
            setFormData((prev) => ({
                ...prev,
                quantity: newQuantity,
                remaining: (Number(prev.remaining) || 0) + diff,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleUpdate = async () => {
        if (!data || !data._id) {
            message.error("No inventory item selected for update.");
            return;
        }
        const dataToSend = {
            ...formData,
            purchaseDate: formData.purchaseDate
                ? dayjs(formData.purchaseDate).format("YYYY-MM-DD")
                : null,
            quantity: Number(formData.quantity),
            remaining: Number(formData.remaining),
            assignedSerialNumbers: assignedSerialNumbers, // include assigned serials in payload
        };
        try {
            const response = await putRequest(`/api/inventory?id=${data._id}`, dataToSend);
            if(response.message=="Inventory item updated successfully") {
                message.success("Inventory updated successfully!");
                onRequestClose(true); // pass true to indicate update
            } else {
                message.error(response.error || "Inventory not updated");
            }
        } catch (error) {
            message.error("Failed to update inventory");
        }
    };

    return (
        <ModalWrapper title="Edit Inventory" open={open} onRequestClose={onRequestClose}>
            <div className="flex flex-col gap-4">
                <div className="max-h-[60vh] overflow-y-auto px-2">
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block mb-1">Product Name</label>
                                <Input
                                    placeholder="Enter product name"
                                    value={formData.productName}
                                    onChange={e => handleInputChange("productName", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Quantity</label>
                                <Input
                                    type="number"
                                    min={1}
                                    placeholder="Enter quantity"
                                    value={formData.quantity}
                                    onChange={e => handleInputChange("quantity", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Product Type</label>
                                <Input
                                    placeholder="Enter product type"
                                    className="w-full"
                                    value={formData.productType}
                                    onChange={e => handleInputChange("productType", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Price</label>
                                <Input
                                    type="number"
                                    min={0}
                                    placeholder="0"
                                    value={formData.price}
                                    onChange={e => handleInputChange("price", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Purchase Date</label>
                                <DatePicker
                                    className="w-full"
                                    value={formData.purchaseDate}
                                    onChange={date => handleInputChange("purchaseDate", date)}
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block mb-1">Serial No</label>
                                <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder="Enter serial numbers and press Enter"
                                    value={allSerialNumbers}
                                    onChange={handleSerialNosChange}
                                    tokenSeparators={[',']}
                                    tagRender={(props) => {
                                        const { label, closable, onClose } = props;
                                        const isAssigned = assignedSerialNumbers.includes(label);
                                        return (
                                            <Tag
                                                color={isAssigned ? "red" : "blue"}
                                                closable={!isAssigned && closable}
                                                onClose={onClose}
                                                style={{ marginRight: 3, opacity: isAssigned ? 0.6 : 1 }}
                                            >
                                                {label} {isAssigned ? "(Assigned)" : ""}
                                            </Tag>
                                        );
                                    }}
                                />
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {allSerialNumbers.map((sn, idx) => {
                                        const isAssigned = assignedSerialNumbers.includes(sn);
                                        return (
                                            <Tag key={idx} color={isAssigned ? "red" : "blue"} style={{ opacity: isAssigned ? 0.6 : 1 }}>
                                                {sn} {isAssigned ? "(Assigned)" : ""}
                                            </Tag>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <label className="block mb-1">Description</label>
                                <TextArea
                                    placeholder="Write description about product..."
                                    rows={4}
                                    value={formData.description}
                                    onChange={e => handleInputChange("description", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button onClick={() => onRequestClose(false)}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={handleUpdate}
                        className="bg-primary hover:bg-primary/80"
                    >
                        Update
                    </Button>
                </div>
            </div>
        </ModalWrapper>
    );
};

export default EditInventoryModal;